import * as ngCore from '@angular/core'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'

import type { TypeParams } from '../../types'

import { getActionsConfig } from './actions'
import { Api } from './api'
import * as effects from './effects'
import * as extraReducers from './extra-reducers'
import { Selectors } from './selectors'
import { State } from './state'

/**
 * Manages space lite record functional usage as a plugin of the records
 * collection.
 *
 */
export class Plugin extends ec.plugins.Plugin<
  TypeParams,
  typeof Plugin.id,
  State,
  Selectors,
  ReturnType<typeof getActionsConfig>
> {
  /** @see {@link ec.plugins.Plugin.Id.Custom} */
  public static id = ec.plugins.Plugin.Id.Custom.create('FunctionalUsagePlugin')

  /** @see {@link Api} */
  public readonly api: Api = ngCore.inject(Api)

  /**
   * Convenience accessor for the feature's action creators.
   * Allows consumers to call `plugin.actions.xyz` instead of `plugin.feature.actions.xyz`.
   */
  public get actions(): Plugin['feature']['actions'] {
    return this.feature.actions
  }

  /**
   * Convenience accessor for the feature's selectors.
   * Allows consumers to call `plugin.selectors.xyz` instead of `plugin.feature.selectors.xyz`.
   */
  public get selectors(): Plugin['feature']['selectors'] {
    return this.feature.selectors
  }

  constructor({ collection }: Readonly<Plugin.constructor.Params>) {
    super({
      actions: getActionsConfig(),
      collection,
      id: Plugin.id,
      initialState: State.INITIAL,
      selectors: () => new Selectors(this),
    })

    this.feature.registerExtraReducers(extraReducers.resetOnContextChange(this))

    this.feature.registerEffects(
      effects.discardOnEditingDiscarded(this),
      effects.executeLoad(this),
      effects.executeSubmit(this),
      effects.loadForCurrentEntity(this),
      effects.loadForCurrentEntityOnContextChange(this),
      effects.loadForCurrentEntityOnEntityChange(this),
      effects.submitOnEditingSubmit(this),
    )
  }
}

export namespace Plugin {
  export namespace constructor {
    export interface Params {
      collection: ec.Collection<TypeParams>
    }
  }
}
